import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { api, ApiError, setUnauthorizedHandler } from "./api.ts";
import type { LoginResponse, Me } from "../shared/types.ts";
import { FullPageSpinner } from "./ui/index.tsx";

interface AuthState {
  me: Me | null;
  /** True until the first /me call settles. */
  loading: boolean;
  isAdmin: boolean;
  login: (username: string, password: string) => Promise<LoginOutcome>;
  register: (username: string, password: string, referralCode?: string) => Promise<void>;
  setupPassword: (setupToken: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  setMe: (me: Me) => void;
}

/** Login either signs in, or (for migrated accounts) asks for a new password. */
export type LoginOutcome = { kind: "ok" } | { kind: "setup"; setupToken: string };

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try { setMe(await api.get<Me>("/me")); }
    catch (e) { if (e instanceof ApiError && e.status === 401) setMe(null); else throw e; }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => setMe(null));
    refresh().catch(() => {}).finally(() => setLoading(false));
    return () => setUnauthorizedHandler(null);
  }, [refresh]);

  const value = useMemo<AuthState>(() => ({
    me,
    loading,
    isAdmin: me?.role === "admin",
    async login(username, password) {
      try {
        const r = await api.post<LoginResponse>("/auth/login", { username, password });
        setMe(r.user);
        return { kind: "ok" };
      } catch (e) {
        if (e instanceof ApiError && e.code === "PASSWORD_SETUP_REQUIRED") return { kind: "setup", setupToken: e.body.setup_token };
        throw e;
      }
    },
    async register(username, password, referralCode) {
      const r = await api.post<LoginResponse>("/auth/register", { username, password, referral_code: referralCode || undefined });
      setMe(r.user);
    },
    async setupPassword(setupToken, password) {
      const r = await api.post<LoginResponse>("/auth/setup-password", { setup_token: setupToken, password });
      setMe(r.user);
    },
    async logout() {
      await api.post("/auth/logout").catch(() => {});
      setMe(null);
    },
    refresh,
    setMe,
  }), [me, loading, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Signed-in user, for components rendered under <RequireAuth>. */
export function useMe(): Me {
  const { me } = useAuth();
  if (!me) throw new Error("useMe used outside <RequireAuth>");
  return me;
}

export function RequireAuth({ children, admin = false }: { children: ReactNode; admin?: boolean }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageSpinner />;
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (admin && me.role !== "admin") return <Navigate to="/home" replace />;
  return <>{children}</>;
}
